export const monthIndex = (month) => {
  if (!/^\d{4}-(0[1-9]|1[0-2])$/.test(month)) return NaN;
  const [year, value] = month.split('-').map(Number);
  return year * 12 + value - 1;
};
export const shiftMonth = (month, offset) => {
  const index = monthIndex(month) + offset;
  return `${Math.floor(index / 12)}-${String((index % 12 + 12) % 12 + 1).padStart(2, '0')}`;
};
export const currentMonth = (now = new Date()) => `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
export const previousMonth = (now = new Date()) => shiftMonth(`${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`, -1);
export const validRange = (from, to) => Number.isFinite(monthIndex(from)) && Number.isFinite(monthIndex(to)) &&
  monthIndex(to) >= monthIndex(from) && monthIndex(to) - monthIndex(from) < 12;
export const validReportRange = (from, to) => validRange(from, to) && from.slice(0, 4) === to.slice(0, 4);
export const coveredMonths = (from, to) => validReportRange(from, to) ?
  Array.from({ length: monthIndex(to) - monthIndex(from) + 1 }, (_, index) => shiftMonth(from, index)) : [];
export const carName = (car) => car.vehicle_name || [car.make || car.Make, car.model || car.Model].filter(Boolean).join(' ') || car.plate_number || car.vehicle_id || car.id;
export const money = (amount, currency = 'USD') => new Intl.NumberFormat('en-US', {
  style: 'currency', currency, minimumFractionDigits: 2, maximumFractionDigits: 2,
}).format(amount);
export const sumAmounts = (items) => items.reduce((cents, item) => cents + Math.round(item.amount * 100), 0) / 100;
export const lineTotals = (statements) => {
  const total_earnings = sumAmounts(statements.flatMap((statement) => statement.earnings));
  const total_cost = sumAmounts(statements.flatMap((statement) => statement.costs));
  return { total_earnings, total_cost, net: Math.round((total_earnings - total_cost) * 100) / 100 };
};
export const totalsDiffer = (a, b) => ['total_earnings', 'total_cost', 'net'].some((key) => Math.abs(a[key] - b[key]) >= 0.005);
export const categoryLabel = (category) => category ? category.replace(/_/g, ' ') : 'Uncategorized';

const csvCell = (value) => {
  let text = String(value ?? '');
  if (/^[\s]*[=+\-@]/.test(text) || /^[\t\r\n]/.test(text)) text = `'${text}`;
  return `"${text.replace(/"/g, '""')}"`;
};
export const exportCsv = (items, from, to) => [
  ['Financial statements', `${from} to ${to}`, 'Currency: USD'],
  ['Investor report entries are itemized income and cost lines from the workbook details. No management fee is recalculated.'],
  ['Period', 'Vehicle ID', 'Vehicle name', 'Plate', 'VIN', 'Source', 'Type', 'Date', 'Category', 'Description', 'Amount (USD)'],
  ...items.map((item) => [
    item.period, item.vehicle_id, item.vehicle_name, item.plate_number, item.vin, item.source, item.type, item.occurred_on,
    item.category, item.description, item.amount,
  ]),
].map((row) => row.map(csvCell).join(',')).join('\r\n');

export const workbookError = (file, period) => {
  if (!Number.isFinite(monthIndex(period))) return 'Select a valid month.';
  if (!file) return 'Choose a workbook.';
  if (!/\.(xlsx|xlsm)$/i.test(file.name)) return 'Use .xlsx or .xlsm. Re-save .xls files in a supported format.';
  if (file.size === 0) return 'The workbook is empty.';
  if (file.size > 20 * 1024 * 1024) return 'The workbook exceeds the 20 MiB limit.';
  return '';
};
export const reportScopeProblem = (report, selection) => {
  const periods = coveredMonths(selection.from, selection.to);
  if (report.import.investor_id !== selection.investorId || report.import.period !== selection.to ||
      JSON.stringify(report.import.periods) !== JSON.stringify(periods) ||
      report.statements.some((statement) => statement.investor_id !== selection.investorId || !periods.includes(statement.period))) {
    return 'The report response does not match the selected investor and covered months. Check deployment and preview again.';
  }
  return '';
};
export const previewProblems = (preview, selection) => {
  const scopeProblem = selection && reportScopeProblem(preview, selection);
  if (scopeProblem) return scopeProblem;
  if (!preview?.statements?.length) return 'The preview contains no statements.';
  if (preview.import.errors.length || preview.import.error_message) return 'Fix the reported row errors and preview again.';
  return '';
};

export const previewMonthlyTotals = (statements) => {
  const months = new Map();
  statements.forEach((statement) => {
    const totals = months.get(statement.period) || { period: statement.period, total_earnings: 0, total_cost: 0, net: 0, vehicle_count: 0 };
    ['total_earnings', 'total_cost', 'net'].forEach((field) => {
      totals[field] += Math.round(statement[field] * 100);
    });
    totals.vehicle_count += 1;
    months.set(statement.period, totals);
  });
  return [...months.values()].sort((a, b) => a.period.localeCompare(b.period)).map((totals) => ({
    ...totals, total_earnings: totals.total_earnings / 100, total_cost: totals.total_cost / 100, net: totals.net / 100,
  }));
};
